<template>
  <div class="">
    <UPopover
      v-model:open="popoverOpen"
      :ui="{ width: 'w-full md:w-2/3 lg:w-96' }"
    >
      <UChip
        size="xl"
        :show="!!unreadNotificationsCount"
        :text="unreadNotificationsCount"
      >
        <button
          aria-label="Notifications"
          class="bg-blue p-2 rounded-full flex items-center justify-center"
        >
          <Icon
            name="heroicons:bell-solid"
            aria-label="Notifications"
            class="text-white"
          />
        </button>
      </UChip>

      <template #panel="{ close }">
        <div
          class="p-6 bg-white  max-h-[50vh] overflow-y-auto"
        >
          <div class="font-bold flex items-center justify-between">
            <p>You have {{ unreadNotificationsCount }} unread notifications</p>
            <UTooltip text="Click to view all notifications">
              <UButton
                variant="ghost"
                icon="heroicons:inbox"
                aria-label="View Notifications"
                @click="openLargeNotifications"
              />
            </UTooltip>
          </div>
          <div
            v-if="unreadNotifications.length"
            class="border-t-2 divide-y divide-slate-200 mt-3"
            @click.stop
          >
            <div
              v-for="note in unreadNotifications"
              :key="`${note.id}`"
              class="flex flex-col  w-full py-1"
            >
              <CollectionNotificationsCard

                class="w-full  text-sm flex flex-wrap items-start gap-3 cursor-pointer"
                v-bind="note"
                @view="viewNotificationFromProfile(note)"
              />
            </div>
          </div>
          <div
            v-if="unreadNotificationsCount > unreadNotifications?.length"
            class="pt-4"
          >
            <UButton

              class="w-full"
              variant="soft"
              icon="heroicons:inbox"
              aria-label="View All Notifications"
              @click="openLargeNotifications"
            >
              View All
            </UButton>
          </div>
        </div>
      </template>
    </UPopover>
    <UModal
      v-model="notificationsOpen"
      :ui="{ width: 'w-full xl:w-2/3 md:max-w-3xl' }"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <UCard
          v-if="!currentNotification"
          :ui="{ ring: '', divide: 'divide-y divide-slate-200', body: { padding: 'sm:p-1 p-2 q !py-2' } }"
        >
          <template #header>
            <div class="flex justify-between gap-2  items-center">
              <div class="flex flex-wrap gap-2 md:gap-6 items-center">
                <HeadingText
                  tag="h4"
                  class="text-blue"
                >
                  Inbox
                </HeadingText>
                <div class="flex items-center gap-2">
                  <UBadge
                    :label="notificationsCount +' messages'"
                    variant="soft"
                  />
                  <UBadge :label="unreadNotificationsCount +' unread'" />
                </div>
              </div>
              <UButton
                icon="heroicons:x-mark-16-solid"
                aria-label="Close Notifications"
                class="text-blue"
                variant="soft"
                @click="notificationsOpen = false"
              />
            </div>
          </template>
          <div
            class="space-y-3"
          >
            <div
              ref="el"
              class="flex flex-col divide-y divide-slate-200 w-full"
            >
              <div
                v-for="notification in notifications"
                :key="`${notification.id}`"
                class="px-2 py-1"
              >
                <CollectionNotificationsFullCard

                  v-bind="notification"
                  @open="viewNotification(notification)"
                />
              </div>
            </div>
          </div>
        </UCard>
        <UCard v-else>
          <template #header>
            <div class="flex justify-between gap-2 items-center">
              <HeadingText
                tag="h4"
                class="text-blue"
              >
                Notification
              </HeadingText>
              <div class="flex gap-2">
                <UButton
                  icon="heroicons:arrow-left"
                  class="text-blue"
                  variant="soft"
                  aria-label="Go Back"
                  @click="currentNotification = false"
                />
                <UButton
                  icon="heroicons:x-mark-16-solid"
                  class="text-blue"
                  variant="soft"
                  aria-label="Close Notification"
                  @click="closeNotifications()"
                />
              </div>
            </div>
          </template>
          <CollectionNotificationsFull
            v-bind="currentNotification"
            @close="currentNotification = false"
          />
        </UCard>
      </transition>
    </UModal>
  </div>
</template>

<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core'
import { ref, useTemplateRef } from 'vue'
import type { MNotification } from '~/types/index'

const unreadNotifications = ref<MNotification[]>([])
const popoverOpen = ref(false)
const { unreadNotificationsCount, loadNextPage, notifications, readMessage, canLoadMore, getUnreadNotifications, getNotifications, notificationsCount } = useNotification()
const notificationsOpen = ref(false)

function viewNotificationFromProfile(notification: Notification) {
  openLargeNotifications()
  viewNotification(notification)
}

function openNotifications() {
  notificationsOpen.value = true
}
function openLargeNotifications() {
  popoverOpen.value = false
  openNotifications()
}

function viewNotification(notification: Notification) {
  currentNotification.value = notification
  readMessage(notification, true)
}

function closeNotifications() {
  currentNotification.value = false
  notificationsOpen.value = false
}

const currentNotification = ref<MNotification | false>(false)

const el = useTemplateRef<HTMLElement>('el')

const { reset } = useInfiniteScroll(
  el,
  async () => loadNextPage(),
  {
    distance: 10,
    canLoadMore: () => {
      return canLoadMore.value
    },
  },
)

watch(popoverOpen, async (val) => {
  if (val) {
    unreadNotifications.value = await getUnreadNotifications()
    getNotifications(1)
  }
})
</script>
